import { TDimensionObject, TItem, TJSONInputItem, TUnit } from '@/types/index.js';
import { formatMeasure } from '@/utils/units.js';

export const loadJSON = async <T = unknown,>(url: string): Promise<T> => {
	try {
		const response = await fetch(url);

		if (!response.ok) {
			throw new Error(`Failed to load JSON: ${response.status} ${response.statusText}`);
		}

		const data: T = await response.json();
		return data;
	} catch (error) {
		console.error('Error loading JSON:', error instanceof Error ? error.message : String(error));
		throw error;
	}
};

export const noop = () => {};

export type TMeasurable = Pick<
	TItem,
	'width' | 'height' | 'depth' | 'widthInferred' | 'heightInferred' | 'depthInferred' | 'unit'
>;

const getPrettyMeasuresFromItem = (item: TMeasurable): TDimensionObject[] => [
	{ dimension: 'w', value: item.widthInferred ? undefined : item.width },
	{ dimension: 'h', value: item.heightInferred ? undefined : item.height },
	{ dimension: 'd', value: item.depthInferred ? undefined : item.depth },
];

const toPrettyMeasure =
	(unit: TUnit | undefined) =>
	({ value }: TDimensionObject): string | null =>
		value ? formatMeasure(value, unit) : null;

export const getPrettyMeasures = (item: TMeasurable): string =>
	getPrettyMeasuresFromItem(item)
		.map(toPrettyMeasure(item.unit))
		.filter((measure): measure is string => measure !== null)
		.join(' × ');

// The value of an item's bar, in its unit, as its label prints it, or null when it has none
export const getBarMeasure = (item: Pick<TItem, 'bar' | 'barStyle'>): string | null =>
	item.bar && item.barStyle ? formatMeasure(item.bar, item.barStyle.unit) : null;

// What an item's label prints for its size: its own `measure` when it has one, such as a country's
// area, else its size
export const getSizeMeasure = (item: TMeasurable & Pick<TItem, 'measure'>): string =>
	item.measure || getPrettyMeasures(item);

// The measures under an item: its size, and its bar's value, which can take the size's place
export const getMeasureLines = (item: TMeasurable & Pick<TItem, 'bar' | 'barStyle' | 'measure'>): string[] =>
	[item.barStyle?.hideSize ? null : getSizeMeasure(item), getBarMeasure(item)].filter(
		(line): line is string => Boolean(line)
	);

export const byName = (name: string) => (item: TJSONInputItem) => item.name === name;
